import { AnalyticsUpIcon } from "@hugeicons-pro/core-duotone-rounded";
import { useLingui } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";
import { formatISO, isValid, parseISO } from "date-fns";
import { DownloadsOverview } from "~/components/charts/downloads-overview";
import { TopCountries } from "~/components/charts/top-countries";
import { TopDevices } from "~/components/charts/top-devices";
import { TopEpisodes } from "~/components/charts/top-episodes";
import { TopPlayers } from "~/components/charts/top-players";
import { ChartFilters } from "~/components/charts/ui/chart-filters";
import { AppContainer } from "~/components/layouts/app/app-container";
import { PageHead } from "~/components/ui/page-head";
import {
	buildQuickFiltersData,
	QUICK_FILTERS,
	type QuickFilter,
	useChartFilters,
} from "~/hooks/user-chart-filters";

type StatisticsSearch = {
	q?: QuickFilter;
	from?: string;
	to?: string;
};

export const Route = createFileRoute("/_app/podcasts/$channelUid/statistics")({
	component: Page,
	validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
		const q =
			typeof search.q === "string" && QUICK_FILTERS.includes(search.q)
				? search.q
				: "7d";
		const quickFilters = buildQuickFiltersData();
		const quickFilter =
			q !== "custom" ? quickFilters.find((f) => f.key === q) : undefined;

		return {
			q,
			from:
				typeof search.from === "string" && isValid(parseISO(search.from))
					? search.from
					: quickFilter?.value?.from
						? formatISO(quickFilter.value.from, { representation: "date" })
						: undefined,
			to:
				typeof search.to === "string" && isValid(parseISO(search.to))
					? search.to
					: quickFilter?.value?.to
						? formatISO(quickFilter.value.to, { representation: "date" })
						: undefined,
		};
	},
});

function Page() {
	const search = Route.useSearch();
	const { channelUid } = Route.useParams();

	const { t } = useLingui();

	const chartFilters = useChartFilters({
		initialFilter: search.q,
		initialDate:
			search.from && search.to
				? { from: new Date(search.from), to: new Date(search.to) }
				: undefined,
	});

	const filtersWithChannelUid = {
		...chartFilters.filters,
		channelUid,
	};

	return (
		<>
			<PageHead
				icon={AnalyticsUpIcon}
				title={t`Podcast Statistics`}
				subtitle={t`Track your podcast growth with insights into your most popular episodes, and audience trends.`}
			/>
			<AppContainer className="my-12">
				<ChartFilters hook={chartFilters} />
				<div className="grid grid-cols-12 gap-6">
					<DownloadsOverview
						className="col-span-full lg:col-span-6"
						filters={filtersWithChannelUid}
					/>
					<TopEpisodes
						className="col-span-full lg:col-span-6"
						filters={filtersWithChannelUid}
					/>
					<TopCountries
						className="col-span-full"
						filters={filtersWithChannelUid}
					/>
					<TopDevices
						className="col-span-full md:col-span-6"
						filters={filtersWithChannelUid}
					/>
					<TopPlayers
						className="col-span-full md:col-span-6"
						filters={filtersWithChannelUid}
					/>
				</div>
			</AppContainer>
		</>
	);
}
